/**
 * Investors Analytics Calculator
 * Based on actual data exploration: 250 investors
 * Focus: investor types, locations, investment activity, AUM distribution
 */

import {
  countBy,
  getTopN,
  getMostCommon,
  calculatePercentage,
  createHistogram,
  calculateStats
} from './chartHelpers';

import {
  removeOutliers,
  applyLogScale,
  calculateLinearRegression,
  shouldUseLogScale,
  normalizeBubbleSizes,
  generateTransformationInfo
} from './dataTransformations';

export const calculateInvestorsAnalytics = (investors, allInvestors, options = {}) => {
  if (!investors || investors.length === 0) {
    return {
      summary: { total: 0, cards: [] },
      charts: []
    };
  }

  const { expanded = false } = options;

  const total = investors.length;
  const topType = getMostCommon(investors, 'investor_type');
  const topLocation = getMostCommon(investors, 'hq_location');

  // Calculate investment statistics
  const investmentStats = calculateStats(investors, 'investments_count');
  const aumStats = calculateStats(investors, 'aum');

  // Summary cards
  const summaryCards = [
    {
      label: 'Total Investors',
      value: total.toLocaleString()
    },
    {
      label: 'Top Type',
      value: topType ? truncateText(topType.value, 12) : 'N/A'
    },
    {
      label: 'Median AUM',
      value: aumStats ? `$${formatAUM(aumStats.median)}` : 'N/A'
    },
    {
      label: 'Active',
      value: calculateActivePercentage(investors) + '%'
    }
  ];

  // Chart 1: Investor Type Breakdown (Pie Chart)
  const typeCounts = countBy(investors, 'investor_type');
  const topTypes = getTopN(typeCounts, 6);

  // Chart 2: Top 10 Investor Locations (Horizontal Bar)
  const locationCounts = countBy(investors, 'hq_location');
  const topLocations = getTopN(locationCounts, 10);

  // Chart 3: Investment Activity Distribution (Histogram)
  const investmentHistogram = createInvestmentHistogram(investors);

  // Chart 4: Investor Status (Donut Chart)
  const statusCounts = countBy(investors, 'investor_status');
  const statuses = Object.entries(statusCounts)
    .map(([name, value]) => ({ name: truncateText(name, 30), value }))
    .sort((a, b) => b.value - a.value);

  // Chart 5: AUM Distribution (Histogram)
  const aumHistogram = createAUMHistogram(investors);

  const charts = [
    {
      id: 'investor_types',
      title: 'Investor Type Breakdown',
      type: 'pie',
      data: topTypes,
      note: `${topType ? topType.value : 'Unknown'} leads (${topType ? calculatePercentage(topType.count, total) : 0}%)`
    },
    {
      id: 'locations',
      title: 'Top 10 Locations',
      type: 'horizontal-bar',
      data: topLocations,
      note: `${Object.keys(locationCounts).length} total locations`
    },
    {
      id: 'investment_activity',
      title: 'Investment Count Distribution',
      type: 'bar',
      data: investmentHistogram,
      note: investmentStats ? `Median: ${investmentStats.median.toFixed(0)} deals` : null
    },
    {
      id: 'investor_status',
      title: 'Investor Status',
      type: 'donut',
      data: statuses,
      note: `${statuses.length} status categories`
    },
    {
      id: 'aum_distribution',
      title: 'AUM Distribution',
      type: 'bar',
      data: aumHistogram,
      note: aumStats ? `Data available for ${aumStats.count}/${total} investors` : 'Limited data'
    }
  ];

  // EXPANDED MODE: Add additional detailed charts
  if (expanded) {
    // Chart 6: Fund Size Distribution (Median)
    if (investors.some(inv => inv.median_fund_size)) {
      const fundSizeRanges = [
        { label: '<$50M', min: 0, max: 50 },
        { label: '$50M-$100M', min: 50, max: 100 },
        { label: '$100M-$250M', min: 100, max: 250 },
        { label: '$250M-$500M', min: 250, max: 500 },
        { label: '$500M-$1B', min: 500, max: 1000 },
        { label: '$1B+', min: 1000, max: 1000000 }
      ];

      const fundSizeData = fundSizeRanges.map(range => ({
        name: range.label,
        value: investors.filter(inv => {
          const size = parseFloat(inv.median_fund_size);
          return size >= range.min && size < range.max;
        }).length
      })).filter(d => d.value > 0);

      if (fundSizeData.length > 0) {
        charts.push({
          id: 'fund_size_distribution',
          title: 'Median Fund Size Distribution',
          type: 'bar',
          data: fundSizeData,
          note: `${fundSizeData.reduce((sum, d) => sum + d.value, 0)} investors with fund size data`
        });
      }
    }

    // Chart 7: Dry Powder Distribution
    const dryPowderRanges = [
      { label: '<$100M', min: 0, max: 100 },
      { label: '$100M-$500M', min: 100, max: 500 },
      { label: '$500M-$1B', min: 500, max: 1000 },
      { label: '$1B-$5B', min: 1000, max: 5000 },
      { label: '$5B+', min: 5000, max: 1000000 }
    ];

    const dryPowderData = dryPowderRanges.map(range => ({
      name: range.label,
      value: investors.filter(inv => {
        const powder = parseFloat(inv.dry_powder);
        return powder >= range.min && powder < range.max;
      }).length
    })).filter(d => d.value > 0);

    if (dryPowderData.length > 0) {
      charts.push({
        id: 'dry_powder_distribution',
        title: 'Available Dry Powder Distribution',
        type: 'bar',
        data: dryPowderData,
        note: `${dryPowderData.reduce((sum, d) => sum + d.value, 0)} investors with dry powder data`
      });
    }

    // Chart 8: Fundraising Status
    if (investors.some(inv => inv.most_likely_fundraising)) {
      const fundraisingCounts = countBy(investors.filter(inv => inv.most_likely_fundraising), 'most_likely_fundraising');
      const fundraisingData = Object.entries(fundraisingCounts)
        .map(([name, value]) => ({ name: truncateText(name, 30), value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);

      if (fundraisingData.length > 0) {
        charts.push({
          id: 'fundraising_status',
          title: 'Current Fundraising Status',
          type: 'pie',
          data: fundraisingData,
          note: `${fundraisingData.reduce((sum, d) => sum + d.value, 0)} investors with fundraising status`
        });
      }
    }

    // Chart 9: Top Locations Detail (Expanded)
    const topLocationsExpanded = getTopN(locationCounts, 20);
    if (topLocationsExpanded.length > 10) {
      charts.push({
        id: 'locations_expanded',
        title: 'Top 20 Investor Locations',
        type: 'horizontal-bar',
        data: topLocationsExpanded,
        note: `Extended view: ${Object.keys(locationCounts).length} total locations`
      });
    }

    // ⭐ ENHANCED SCATTER PLOTS - High Quality Data (90%+) ⭐

    // Chart 10: Investment Activity vs Assets Under Management (90% data - EXCELLENT)
    let activityAumRaw = investors
      .filter(inv => inv.investments_count && inv.aum && inv.median_fund_size)
      .map(inv => ({
        name: truncateText(inv.name, 25),
        x: parseFloat(inv.investments_count),
        y: parseFloat(inv.aum),
        investmentCount: parseFloat(inv.investments_count),
        aum: parseFloat(inv.aum),
        bubbleSize: parseFloat(inv.median_fund_size) // 3rd dimension
      }))
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0 && d.y > 0)
      .slice(0, 150);

    if (activityAumRaw.length > 10) {
      // Remove extreme outliers (IQR method, multiplier 3.0 for only extremes)
      const { cleaned: activityAumCleaned, stats: outlierStatsAUM } = removeOutliers(
        activityAumRaw,
        'y', // Remove AUM outliers (BlackRock at $10.5T)
        3.0 // More lenient - only remove extreme outliers
      );

      // Apply log scale (AUM ranges from millions to trillions)
      const activityAumLog = applyLogScale(activityAumCleaned, 'x', 'y');

      // Add bubble sizes (normalized)
      const activityAumData = normalizeBubbleSizes(activityAumLog, 'bubbleSize', 30, 150);

      // Calculate regression
      const regression = calculateLinearRegression(activityAumLog, 'x', 'y');

      // Generate transformation info
      const transformInfo = generateTransformationInfo({
        chartId: 'activity_aum_scatter',
        chartTitle: 'Investment Activity vs Assets Under Management',
        outlierStats: outlierStatsAUM,
        useLogScale: true,
        regression,
        bubbleField: 'Median Fund Size',
        sampleSize: `${activityAumData.length} of ${activityAumRaw.length} investors`
      });

      charts.push({
        id: 'activity_aum_scatter',
        title: 'Investment Activity vs Assets Under Management',
        type: 'scatter',
        data: activityAumData,
        xLabel: 'Number of Investments (log₁₀)',
        yLabel: 'AUM ($M, log₁₀)',
        note: `${activityAumData.length} investors • 90% data • ${outlierStatsAUM?.outlierCount || 0} outliers removed`,
        dataQuality: 'excellent',
        useLogScale: true,
        regression,
        transformInfo
      });
    }

    // Chart 11: Fund Strategy Matrix - Min vs Max Fund Size (91% data - EXCELLENT)
    let fundStrategyRaw = investors
      .filter(inv => inv.min_fund_size && inv.max_fund_size && inv.investments_count)
      .map(inv => ({
        name: truncateText(inv.name, 25),
        x: parseFloat(inv.min_fund_size),
        y: parseFloat(inv.max_fund_size),
        minFund: parseFloat(inv.min_fund_size),
        maxFund: parseFloat(inv.max_fund_size),
        bubbleSize: parseFloat(inv.investments_count) // 3rd dimension: activity level
      }))
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0 && d.y > 0)
      .slice(0, 150);

    if (fundStrategyRaw.length > 10) {
      // Remove outliers from max fund size
      const { cleaned: fundStrategyCleaned, stats: outlierStatsFund } = removeOutliers(
        fundStrategyRaw,
        'y',
        2.5 // Moderate outlier removal
      );

      // Apply log scale (fund sizes range widely)
      const fundStrategyLog = applyLogScale(fundStrategyCleaned, 'x', 'y');

      // Add bubble sizes
      const fundStrategyData = normalizeBubbleSizes(fundStrategyLog, 'bubbleSize', 30, 150);

      // Calculate regression
      const regression = calculateLinearRegression(fundStrategyLog, 'x', 'y');

      const transformInfo = generateTransformationInfo({
        chartId: 'fund_strategy_scatter',
        chartTitle: 'Fund Size Strategy: Min vs Max',
        outlierStats: outlierStatsFund,
        useLogScale: true,
        regression,
        bubbleField: 'Investment Count',
        sampleSize: `${fundStrategyData.length} of ${fundStrategyRaw.length} investors`
      });

      charts.push({
        id: 'fund_strategy_scatter',
        title: 'Fund Size Strategy: Min vs Max',
        type: 'scatter',
        data: fundStrategyData,
        xLabel: 'Min Fund Size ($M, log₁₀)',
        yLabel: 'Max Fund Size ($M, log₁₀)',
        note: `${fundStrategyData.length} investors • 91% data • ${outlierStatsFund?.outlierCount || 0} outliers removed`,
        dataQuality: 'excellent',
        useLogScale: true,
        regression,
        transformInfo
      });
    }

    // Chart 12: Investment Capacity - Median Fund Size vs Dry Powder (45% data)
    let capacityRaw = investors
      .filter(inv => inv.median_fund_size && inv.dry_powder && inv.aum)
      .map(inv => ({
        name: truncateText(inv.name, 25),
        x: parseFloat(inv.median_fund_size),
        y: parseFloat(inv.dry_powder),
        medianFund: parseFloat(inv.median_fund_size),
        dryPowder: parseFloat(inv.dry_powder),
        bubbleSize: parseFloat(inv.aum) // 3rd dimension: total assets
      }))
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0 && d.y > 0)
      .slice(0, 150);

    if (capacityRaw.length > 10) {
      // Remove outliers from both axes
      const { cleaned: capacityCleanedY, stats: outlierStatsCapacity } = removeOutliers(
        capacityRaw,
        'y',
        2.5
      );

      // Apply log scale
      const capacityLog = applyLogScale(capacityCleanedY, 'x', 'y');

      // Add bubble sizes
      const capacityData = normalizeBubbleSizes(capacityLog, 'bubbleSize', 30, 150);

      // Calculate regression
      const regression = calculateLinearRegression(capacityLog, 'x', 'y');

      const transformInfo = generateTransformationInfo({
        chartId: 'capacity_scatter',
        chartTitle: 'Investment Capacity Analysis',
        outlierStats: outlierStatsCapacity,
        useLogScale: true,
        regression,
        bubbleField: 'Total AUM',
        sampleSize: `${capacityData.length} of ${capacityRaw.length} investors`
      });

      charts.push({
        id: 'capacity_scatter',
        title: 'Investment Capacity Analysis',
        type: 'scatter',
        data: capacityData,
        xLabel: 'Median Fund Size ($M, log₁₀)',
        yLabel: 'Dry Powder ($M, log₁₀)',
        note: `${capacityData.length} investors • 45% data • ${outlierStatsCapacity?.outlierCount || 0} outliers removed`,
        dataQuality: 'moderate',
        useLogScale: true,
        regression,
        transformInfo
      });
    }
  }

  return {
    summary: {
      total,
      filtered: allInvestors && total !== allInvestors.length ? total : null,
      cards: summaryCards
    },
    charts: charts.filter(chart => chart.data && chart.data.length > 0)
  };
};

// Helper: Create investment count histogram
const createInvestmentHistogram = (investors) => {
  const bins = [
    { label: '1-2 deals', min: 1, max: 2 },
    { label: '3-5 deals', min: 3, max: 5 },
    { label: '6-10 deals', min: 6, max: 10 },
    { label: '11-20 deals', min: 11, max: 20 },
    { label: '21-50 deals', min: 21, max: 50 },
    { label: '50+ deals', min: 51, max: 1000 }
  ];

  return bins.map(bin => ({
    name: bin.label,
    value: investors.filter(inv => {
      const count = inv.investments_count;
      return count >= bin.min && count <= bin.max;
    }).length
  })).filter(bin => bin.value > 0);
};

// Helper: Create AUM histogram
const createAUMHistogram = (investors) => {
  const investorsWithAUM = investors.filter(inv => inv.aum && !isNaN(inv.aum) && inv.aum > 0);

  if (investorsWithAUM.length === 0) return [];

  const bins = [
    { label: '<$100M', min: 0, max: 100 },
    { label: '$100M-$500M', min: 100, max: 500 },
    { label: '$500M-$1B', min: 500, max: 1000 },
    { label: '$1B-$5B', min: 1000, max: 5000 },
    { label: '$5B-$10B', min: 5000, max: 10000 },
    { label: '$10B+', min: 10000, max: 1000000 }
  ];

  return bins.map(bin => ({
    name: bin.label,
    value: investorsWithAUM.filter(inv => {
      const aum = parseFloat(inv.aum);
      return aum >= bin.min && aum < bin.max;
    }).length
  })).filter(bin => bin.value > 0);
};

// Helper: Calculate percentage of active investors
const calculateActivePercentage = (investors) => {
  const activeCount = investors.filter(inv => {
    const status = (inv.investor_status || '').toLowerCase();
    return status.includes('active') || status.includes('seeking');
  }).length;

  return investors.length > 0
    ? Math.round((activeCount / investors.length) * 100)
    : 0;
};

// Helper: Format AUM for display
const formatAUM = (aum) => {
  if (!aum || isNaN(aum)) return '0';

  if (aum >= 1000) {
    return `${(aum / 1000).toFixed(1)}B`;
  } else if (aum >= 1) {
    return `${aum.toFixed(0)}M`;
  }
  return `${(aum * 1000).toFixed(0)}K`;
};

// Helper: Truncate text
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};
